import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';

const CRMContext = createContext();

export const CRMProvider = ({ children }) => {
  const { user } = useAuth();
  
  const [policies, setPolicies] = useState([]);
  const [activities, setActivities] = useState([]);
  const [policyTypes, setPolicyTypes] = useState([{ id: 1, name: 'Cargando...', color: '#ccc' }]);
  const [loadingCRM, setLoadingCRM] = useState(true);

  // Carga Inicial
  useEffect(() => {
    if (user) {
      fetchCoreData();
    }
  }, [user]);

  const fetchCoreData = async () => {
    try {
      setLoadingCRM(true);
      
      // 1. Fetch Pólizas
      const { data: polData, error: polErr } = await supabase
        .from('policies')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (!polErr && polData) {
        // Adaptar las claves DDBB a camelCase para React si es necesario
        const adaptedPolicies = polData.map(p => ({
          ...p,
          clientName: p.client_name,
          email: p.contact_email,
          date: new Date(p.created_at).toLocaleDateString(),
          value: typeof p.value === 'number' ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(p.value) : p.value
        }));
        setPolicies(adaptedPolicies);
      }

      // 2. Fetch Actividades 
      // Unimos (join) con la tabla profiles para mostrar el nombre y rol del autor
      const { data: actData, error: actErr } = await supabase
        .from('activities')
        .select(`
          id,
          log_text,
          created_at,
          profiles ( name, role )
        `)
        .order('created_at', { ascending: false })
        .limit(20);

      if (!actErr && actData) {
        const adaptedActs = actData.map(a => ({
          id: a.id,
          text: a.profiles 
                ? `${a.profiles.name} (${a.profiles.role}): ${a.log_text}` 
                : a.log_text,
          time: new Date(a.created_at).toLocaleString()
        }));
        setActivities(adaptedActs);
      }

      // 3. Fetch Policy Types
      const { data: typeData, error: typeErr } = await supabase
        .from('policy_types')
        .select('*')
        .order('name', { ascending: true });
        
      if (!typeErr && typeData && typeData.length > 0) {
        setPolicyTypes(typeData);
      } else {
        // Fallback genérico visual si la tabla aún no tiene datos o hay error de lectura
        setPolicyTypes([
          { id: '1', name: 'Auto', color: 'var(--primary)' },
          { id: '2', name: 'Vida', color: '#38bdf8' },
          { id: '3', name: 'Hogar', color: '#818cf8' },
          { id: '4', name: 'Salud', color: 'var(--success)' }
        ]);
      }

    } catch (err) {
      console.error("Error sincronizando VeritasCRM BBDD", err);
    } finally {
      setLoadingCRM(false);
    }
  };

  const addPolicy = async (newPolicy) => {
    try {
      // Optimistic Render
      const tempId = crypto.randomUUID();
      const tempRef = {
        ...newPolicy,
        id: tempId,
        email: newPolicy.email || null,
        stage: 'lead',
        date: new Date().toLocaleDateString(),
        value: newPolicy.value || 0
      };
      setPolicies([tempRef, ...policies]);

      // Call Supabase
      const { data, error } = await supabase.from('policies').insert({
        client_name: newPolicy.clientName,
        type: newPolicy.type,
        value: parseInt(newPolicy.value?.toString().replace(/[^0-9]/g, ''), 10) || 0,
        value_uf: newPolicy.value_uf || null,
        phone: newPolicy.phone,
        contact_email: newPolicy.email,
        assigned_to: user.id
      }).select().single();

      if (!error && data) {
        // Reemplazamos el TempID por el ID real y arreglamos formato
        const formattedValue = typeof data.value === 'number' 
          ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(data.value) 
          : data.value;

        setPolicies(prev => prev.map(p => p.id === tempId ? {
          ...data,
          clientName: data.client_name,
          date: new Date(data.created_at).toLocaleDateString(),
          value: formattedValue
        } : p));
        
        await logActivity(`Ingresó al prospecto comercial ${newPolicy.clientName}.`);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const importPolicies = async (policiesArray) => {
    try {
      // Formatear payload para la BBDD de supabase
      const dbPayload = policiesArray.map(p => ({
        client_name: p.clientName,
        type: p.type,
        value: parseInt(p.value?.toString().replace(/[^0-9]/g, ''), 10) || 0,
        phone: p.phone,
        contact_email: p.email,
        assigned_to: user.id
      }));

      // Supabase Mass Insert
      const { data, error } = await supabase.from('policies').insert(dbPayload).select();

      if (!error && data) {
        // Formatear para Optimistic UI
        const mappedResults = data.map(d => ({
          ...d,
          clientName: d.client_name,
          email: d.contact_email,
          date: new Date(d.created_at).toLocaleDateString(),
          value: typeof d.value === 'number' 
            ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(d.value) 
            : d.value
        }));

        setPolicies(prev => [...mappedResults, ...prev]);
        await logActivity(`Procesó una ingesta de datos masiva de ${data.length} planillas (Data Hub CSV).`);
      } else {
        console.error("Error importando pólizas masivas", error);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const editPolicy = async (id, updatedFields) => {
    try {
      // Optimistic Render
      const oldPolicy = policies.find(p => p.id === id);
      setPolicies(prev => prev.map(p => p.id === id ? { ...p, ...updatedFields } : p));

      // Payload translation to DB Schema
      const payload = {};
      if (updatedFields.clientName) payload.client_name = updatedFields.clientName;
      if (updatedFields.type) payload.type = updatedFields.type;
      if (updatedFields.value) payload.value = parseInt(updatedFields.value.toString().replace(/[^0-9]/g, ''), 10) || 0;
      if (updatedFields.value_uf !== undefined) payload.value_uf = updatedFields.value_uf;
      if (updatedFields.phone) payload.phone = updatedFields.phone;

      // Supabase Update
      const { data, error } = await supabase.from('policies')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (!error && data) {
         const formattedValue = typeof data.value === 'number' 
          ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(data.value) 
          : data.value;

         setPolicies(prev => prev.map(p => p.id === id ? {
          ...data,
          clientName: data.client_name,
          email: data.contact_email,
          date: new Date(data.created_at).toLocaleDateString(),
          value: formattedValue
        } : p));

        await logActivity(`Editó los datos de la póliza de ${payload.client_name || oldPolicy?.clientName}.`);
      } else {
        console.error("Error editando póliza", error);
        // Rollback on fail
        setPolicies(prev => prev.map(p => p.id === id ? oldPolicy : p));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const logActivity = async (actionDesc) => {
    try {
      // Optimistic local update
      const act = {
        id: Date.now(),
        text: `${user.name} (${user.role}): ${actionDesc}`,
        time: 'Justo ahora'
      };
      setActivities([act, ...activities]);

      // Insert real
      await supabase.from('activities').insert({
        log_text: actionDesc,
        created_by: user.id
      });
    } catch (err) {
      console.error(err);
    }
  };

  const updatePolicyStage = async (policyId, targetStage) => {
    try {
      // Optimistic Update
      const oldStage = policies.find(p => p.id === policyId)?.stage;
      setPolicies(prev => prev.map(p => p.id === policyId ? { ...p, stage: targetStage } : p));

      // Supabase Update
      const { error } = await supabase.from('policies')
        .update({ stage: targetStage })
        .eq('id', policyId);

      if (error) {
        console.error("No tienes permisos para modificar este registro", error);
        // Rollback
        setPolicies(prev => prev.map(p => p.id === policyId ? { ...p, stage: oldStage } : p));
      } else {
        await logActivity(`Trasladó un expediente (ID: ${policyId.substring(0,6)}...) a estado: ${targetStage}.`);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const clearActivities = () => setActivities([]);
  const deleteActivity = (id) => setActivities(activities.filter(a => a.id !== id));

  // --- MANTENEDOR DE TIPOS DE PÓLIZAS DINÁMICAS ---
  const addPolicyType = async (typePayload) => {
    try {
      const { data, error } = await supabase.from('policy_types').insert(typePayload).select().single();
      if (!error && data) {
        setPolicyTypes(prev => [...prev, data].sort((a,b) => a.name.localeCompare(b.name)));
        await logActivity(`Creó un nuevo formato de seguro comercial: ${data.name}`);
      }
      return { data, error };
    } catch (err) {
      console.error(err);
      return { error: err };
    }
  };

  const deletePolicyType = async (id, name) => {
    try {
      const { error } = await supabase.from('policy_types').delete().eq('id', id);
      if (!error) {
        setPolicyTypes(prev => prev.filter(t => t.id !== id));
        await logActivity(`Eliminó el formato de seguro corporativo: ${name}`);
      }
      return { error };
    } catch(err) {
      console.error(err);
      return { error: err };
    }
  };

  return (
    <CRMContext.Provider value={{ 
      policies, 
      activities, 
      policyTypes,
      loadingCRM, 
      addPolicy,
      importPolicies, 
      editPolicy,
      updatePolicyStage, 
      logActivity, 
      clearActivities, 
      deleteActivity,
      addPolicyType,
      deletePolicyType
    }}>
      {children}
    </CRMContext.Provider>
  );
};

export const useCRM = () => useContext(CRMContext);
